import { html, nothing } from 'lit';
// eslint-disable-next-line import/extensions
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { connect } from 'pwa-helpers/connect-mixin';
import LitElementExtended from '../classes/LitElementExtended';
import './tef-coms-consent';
import store from '../store';
import {
  deny, oneclick, setActiveGroupInd, setCollpsibleOpen,
} from '../store/consents';
import {
  TRACKER_EVENT_NAME_START_CHANGE_PERMISSION,
  TRACKER_EVENT_NAME_CLICK_COLLAPSABLE,
} from '../service/tracker';
import service from '../service';
import { haveUncheckedFeatures } from '../util/consents';
import { ConsentGroup, ConsentType } from '../types/common';

import TemplateResolver from './templates/templateResolver';

export class TefComsConsentGroup extends connect(store)(LitElementExtended) {
  consentGroup?: ConsentGroup;
  groupIndex = -1;
  mode = '';

  constructor() {
    super();
    this.consentGroup = undefined;
    this.groupIndex = -1;
    this.mode = '';

    this.addEventListener('occl.collapsible.open', () => {
      service.tracker.doTracking(TRACKER_EVENT_NAME_CLICK_COLLAPSABLE, {
        group: this.groupName,
        subject: this.mode && this.mode.includes('multiedit') ? 'consent' : 'oneclick',
      });
      store.dispatch(setCollpsibleOpen(this.groupName));
    });
  }

  createRenderRoot() {
    return this; // no shadow DOM for sub-components
  }

  updated() {
    const { oneclick: oneclick1, consents } = this.consentGroup;

    // calculate oneclick
    if (oneclick1 && !this.mode.includes('multiedit')) {
      // calculate oneclick checkbox: true only if all granted
      const inGroupIsSomethingUnchecked = haveUncheckedFeatures(consents)
        || !!consents.find((c) => !c.granted);
      const oneclickCheck: HTMLInputElement = this.querySelector(`#oneclick_check_${this.groupIndex}`);
      oneclickCheck.checked = !inGroupIsSomethingUnchecked;
    }

    this.replaceElements();
  }

  static get properties() {
    return {
      consentGroup: { type: Object },
      groupIndex: { type: Number },
      mode: { type: String },
    };
  }

  get noConsentSet() {
    return !this.consentGroup.consents.find((co) => co.granted);
  }

  oneclickAction() {
    const oneclickCheck: HTMLInputElement = this.querySelector(`#oneclick_check_${this.groupIndex}`);
    const { checked } = oneclickCheck;
    store.dispatch(oneclick(this.groupIndex, checked));
  }

  denyAction() {
    const denyCheck: HTMLInputElement = this.querySelector(`#deny_check_${this.groupIndex}`);
    const { checked } = denyCheck;
    store.dispatch(deny(this.groupIndex, checked));
  }

  get groupOneclickText() {
    const checkBoxId = `oneclick_check_${this.groupIndex}`;

    const clickTextTemplate = TemplateResolver.getInstance().resolve('clickTextTemplate');

    return clickTextTemplate(checkBoxId, this);
  }

  get groupText() {
    const { text } = this.consentGroup;

    return text ? html` ${unsafeHTML(text)} ` : nothing;
  }

  get groupName() {
    return this.consentGroup && this.consentGroup.name ? this.consentGroup.name : 'noname';
  }

  get groupOneclickConsentsContainer() {
    const { config: { stylecontext = '' }, consents: { collapsibleOpenGroupName } } = store.getState();

    const clickConsentsContainerTemplate = TemplateResolver.getInstance().resolve('clickConsentsContainerTemplate');

    return clickConsentsContainerTemplate(stylecontext, this, collapsibleOpenGroupName);
  }

  groupConsents(layout = 'default') {
    const { consents, required } = this.consentGroup;

    function filterPredicator(consent: ConsentType): boolean {
      return !consent.deprecated && !(consent.modeconstraint === 'view');
    }

    if (required) {
      const groupConsentsTemplate = TemplateResolver.getInstance().resolve('groupConsentsTemplate');
      return groupConsentsTemplate(this);
    }

    return html`${consents
      .filter(filterPredicator)
      .map(
        (consent) => html`
              <tef-coms-consent .consent=${consent} ?required=${required} .layout=${layout}></tef-coms-consent>
            `,
      ) /* eslint-enable indent */}`;
  }

  get withdrawal() {
    const { denyall, oneclick: denyOneclick } = this.consentGroup;

    if (!denyall || denyOneclick) {
      return null;
    }

    const checkboxId = `deny_check_${this.groupIndex}`;

    const withdrawalTemplate = TemplateResolver.getInstance().resolve('withdrawalTemplate');

    return withdrawalTemplate(this, checkboxId);
  }

  openGroupForEdit(e: MouseEvent) {
    store.dispatch(setActiveGroupInd(this.groupIndex));
    service.tracker.doTracking(TRACKER_EVENT_NAME_START_CHANGE_PERMISSION, {
      group: this.groupName,
    });
    e.preventDefault();
  }

  get viewTemplate() {
    const {
      consents: { consentGroupsView },
      config: { stylecontext = '' },
    } = store.getState();

    const { consents } = consentGroupsView[this.groupIndex];

    const filteredConsents = consents
      .filter(({ granted = false }) => granted === true)
      .filter(({ modeconstraint }) => !(modeconstraint === 'edit'));

    const groupViewTemplate = TemplateResolver.getInstance().resolve('groupViewTemplate');

    return groupViewTemplate(this, consentGroupsView, filteredConsents, stylecontext);
  }

  get template() {
    return TemplateResolver.getInstance().resolve('groupTemplate')(this);
  }

  render() {
    return html`
      ${this.mode && this.mode.includes('multiedit') ? this.viewTemplate : this.template}
    `;
  }
}

export default TefComsConsentGroup;
window.customElements.define('tef-coms-consent-group', TefComsConsentGroup);
